import type { Court } from "@/lib/courtData";
import { CourtEnum } from "@/lib/courtEnum";
import { ExpertiseEnum } from "@/lib/expertiseEnum";
import {
  EXPERTISE_FEATURED_AWARD_IDS,
  resolveFeaturedAwards,
} from "@/lib/featuredAwards";
import {
  type ExpertiseCityStringSet,
  type InsolvenzBericht,
  type InsolvenzSection,
} from "@/lib/expertise/expertiseCityTypes";
import { getMessages } from "@/lib/i18n/messages";
import { mergeDeep } from "@/lib/i18n/mergeDeep";
import type { Locale } from "@/lib/i18n/locales";
import { LocationEnum } from "@/lib/locationEnum";
import { expertiseCityWiring } from "@/lib/strings/expertiseCityWiring";
import { TeamMemberEnum } from "@/lib/teamData";
import { deExpertiseCity } from "@/messages/de/expertiseCityCatalog";
import courtRegistry from "@/messages/de/expertiseCity/courtRegistry.json";
import { enExpertiseCityOverlay } from "@/messages/en/enExpertiseCityOverlay";
import { frExpertiseCityOverlay } from "@/messages/fr/frExpertiseCityOverlay";
import { jaExpertiseCityOverlay } from "@/messages/ja/jaExpertiseCityOverlay";
import { zhExpertiseCityOverlay } from "@/messages/zh/zhExpertiseCityOverlay";

const localeExpertiseCityOverlays: Partial<
  Record<Locale, Record<string, unknown>>
> = {
  en: enExpertiseCityOverlay as unknown as Record<string, unknown>,
  fr: frExpertiseCityOverlay as unknown as Record<string, unknown>,
  ja: jaExpertiseCityOverlay as unknown as Record<string, unknown>,
  zh: zhExpertiseCityOverlay as unknown as Record<string, unknown>,
};

const processedExpertiseCityByLocale = new Map<
  Locale,
  Record<string, unknown>
>();

const COURT_SHELL_KEYS = [
  "name",
  "sitz",
  "url",
  "image",
  "imageAlt",
  "imageLicence",
] as const;

type CourtRegistryShell = Omit<Court, "enum" | "description">;

const getLatestMonth = (insolvenzberichte: InsolvenzBericht[]): string => {
  const latestMonth = insolvenzberichte.sort(
    (a, b) => new Date(b.month).getTime() - new Date(a.month).getTime(),
  )[0].month;
  return new Date(latestMonth).toLocaleDateString("de-DE", {
    month: "long",
    year: "numeric",
  });
};

const getLatestIntro = (insolvenzberichte: InsolvenzBericht[]): string => {
  return insolvenzberichte.sort(
    (a, b) => new Date(b.month).getTime() - new Date(a.month).getTime(),
  )[0].intro;
};

function reviveTeamMembers(value: unknown): unknown {
  if (value === null || typeof value !== "object") {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(reviveTeamMembers);
  }
  const obj = value as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (k === "teamMembers" && Array.isArray(v)) {
      out[k] = v.map((m) => m as TeamMemberEnum);
    } else {
      out[k] = reviveTeamMembers(v);
    }
  }
  return out;
}

function isLegacyFullCourtRow(row: Record<string, unknown>): boolean {
  return (
    typeof row.name === "string" &&
    typeof row.url === "string" &&
    typeof row.image === "string"
  );
}

function resolveCourtRow(row: unknown): Court {
  if (!row || typeof row !== "object") {
    throw new Error("Invalid court row");
  }
  const r = row as Record<string, unknown>;

  if (isLegacyFullCourtRow(r)) {
    return {
      ...(r as unknown as Court),
      enum: r.enum as CourtEnum,
    };
  }

  const en = r.enum;
  if (typeof en !== "string") {
    throw new Error("Court row missing enum");
  }

  const base = courtRegistry[en as keyof typeof courtRegistry] as
    | CourtRegistryShell
    | undefined;
  if (!base) {
    throw new Error(`Missing courtRegistry entry for enum: ${en}`);
  }

  const merged: Record<string, unknown> = { ...base };
  for (const k of COURT_SHELL_KEYS) {
    if (k in r && r[k] !== undefined) {
      merged[k] = r[k];
    }
  }

  const description = typeof r.description === "string" ? r.description : "";

  return {
    enum: en as CourtEnum,
    name: merged.name as string,
    sitz: merged.sitz as string,
    url: merged.url as string,
    image: merged.image as string,
    imageAlt: merged.imageAlt as string,
    imageLicence: (merged.imageLicence ?? {}) as Court["imageLicence"],
    description,
  };
}

function resolveCourtsInExpertiseCityTree(root: Record<string, unknown>): void {
  for (const cities of Object.values(root)) {
    if (
      typeof cities !== "object" ||
      cities === null ||
      Array.isArray(cities)
    ) {
      continue;
    }
    for (const city of Object.values(cities as Record<string, unknown>)) {
      if (typeof city !== "object" || city === null || Array.isArray(city)) {
        continue;
      }
      const c = city as Record<string, unknown>;
      if (!Array.isArray(c.courts)) {
        continue;
      }
      c.courts = c.courts.map((item) => resolveCourtRow(item));
    }
  }
}

function applyFeaturedAwards(
  root: Record<string, unknown>,
  locale: Locale,
): void {
  for (const [expertiseKey, awardIds] of Object.entries(
    EXPERTISE_FEATURED_AWARD_IDS,
  )) {
    const cities = root[expertiseKey];
    if (
      typeof cities !== "object" ||
      cities === null ||
      Array.isArray(cities)
    ) {
      continue;
    }

    const resolved = resolveFeaturedAwards(awardIds, locale);
    if (!resolved?.length) {
      continue;
    }

    for (const city of Object.values(cities as Record<string, unknown>)) {
      if (typeof city !== "object" || city === null || Array.isArray(city)) {
        continue;
      }
      (city as Record<string, unknown>).featuredAwards = resolved;
    }
  }
}

function applyExpertiseCityWiring(root: Record<string, unknown>): void {
  for (const [expertiseKey, cities] of Object.entries(root)) {
    const bySlug = expertiseCityWiring[expertiseKey as ExpertiseEnum];
    if (!bySlug) {
      throw new Error(
        `Missing expertiseCityWiring for expertise: ${expertiseKey}`,
      );
    }
    if (
      typeof cities !== "object" ||
      cities === null ||
      Array.isArray(cities)
    ) {
      continue;
    }
    for (const city of Object.values(cities as Record<string, unknown>)) {
      if (typeof city !== "object" || city === null || Array.isArray(city)) {
        continue;
      }
      const c = city as Record<string, unknown>;
      const slug = c.slug;
      if (typeof slug !== "string") {
        continue;
      }
      const entry = bySlug[slug];
      if (!entry) {
        throw new Error(
          `Missing expertiseCityWiring for ${expertiseKey}/${slug}`,
        );
      }
      c.teamMembers = [...entry.teamMembers];
    }
  }
}

function patchInsolvencyTitles(
  root: Record<string, unknown>,
  locale: Locale,
): void {
  const ins = root.insolvenzrecht as Record<string, unknown> | undefined;
  if (!ins) return;

  const titles = getMessages(locale).common.insolvencyMonitor;

  const koeln = ins.Köln as
    | { insolvenzenSection?: InsolvenzSection }
    | undefined;
  if (koeln?.insolvenzenSection?.insolvenzberichte?.length) {
    const berichte = koeln.insolvenzenSection.insolvenzberichte;
    koeln.insolvenzenSection.title = titles.koelnTitle.replace(
      "{month}",
      getLatestMonth(berichte),
    );
    koeln.insolvenzenSection.description = getLatestIntro(berichte);
  }

  const koblenz = ins.Koblenz as
    | { insolvenzenSection?: InsolvenzSection }
    | undefined;
  if (koblenz?.insolvenzenSection?.insolvenzberichte?.length) {
    const berichte = koblenz.insolvenzenSection.insolvenzberichte;
    koblenz.insolvenzenSection.title = titles.koblenzTitle.replace(
      "{month}",
      getLatestMonth(berichte),
    );
    koblenz.insolvenzenSection.description = getLatestIntro(berichte);
  }
}

function buildProcessedExpertiseCityTree(
  locale: Locale,
): Record<string, unknown> {
  let root = JSON.parse(JSON.stringify(deExpertiseCity)) as Record<
    string,
    unknown
  >;
  const overlay = localeExpertiseCityOverlays[locale];
  if (overlay) {
    root = mergeDeep(root, overlay);
  }
  applyExpertiseCityWiring(root);
  const revived = reviveTeamMembers(root) as Record<string, unknown>;
  applyFeaturedAwards(revived, locale);
  patchInsolvencyTitles(revived, locale);
  resolveCourtsInExpertiseCityTree(revived);
  return revived;
}

export function getExpertiseCityStringsForLocale(
  locale: Locale,
): Record<
  ExpertiseEnum,
  Partial<Record<LocationEnum, ExpertiseCityStringSet>>
> {
  let tree = processedExpertiseCityByLocale.get(locale);
  if (!tree) {
    tree = buildProcessedExpertiseCityTree(locale);
    processedExpertiseCityByLocale.set(locale, tree);
  }
  return tree as unknown as Record<
    ExpertiseEnum,
    Partial<Record<LocationEnum, ExpertiseCityStringSet>>
  >;
}
